import Instance from "./types/Instance";
import Series from "./types/Series";
import {
  detectDisplaySets,
  formatMatrixKey,
  framesShareOnePlaneFromDicomJson,
  numberDisplaySetLabels,
  type DisplaySetHeaders,
} from "./display-set-rules";

export { formatMatrixKey };

/**
 * Splitting a DICOM series into the units the editor treats independently.
 *
 * A series is not a display unit. It usually behaves like one for CT/MR, where the
 * instances share a matrix and the stack is meaningful, but nothing in the
 * standard requires a series to be homogeneous — and in practice it often isn't.
 * Ultrasound routinely puts 2D stills, colour Doppler, M-mode strips, cine loops
 * and rendered report screens under a single SeriesInstanceUID; MR is not immune
 * either, since scanners append their own projection/report frames to a series at
 * a different size from the slices. Every viewer therefore derives its own
 * grouping (OHIF calls the result a "display set"); this is ours.
 *
 * Two reasons it matters here, one of them a safety property:
 *
 *  - Redaction rectangles are absolute pixel coordinates committed to a whole
 *    series at once (see redaction-store). A part that mixed matrix sizes would
 *    apply a box drawn over the banner of a 636x434 still to an 800x600 image,
 *    where it under-covers. Splitting by matrix is what makes the committed
 *    rectangle mean the same thing for every instance it lands on.
 *  - The viewer displays a series as one scrollable stack at one zoom. A cine and
 *    a pile of unrelated stills in the same stack makes trim, scroll and
 *    fit-to-window all nonsense.
 *
 * The split is a *display and edit* grouping only. `seriesInstanceUID` is left
 * alone and shared between the parts — DICOM identity is not ours to rewrite, and
 * the export collapses the parts back into one series record before upload (see
 * `collapseSeriesParts`).
 *
 * The rules themselves — what counts as a clip, how the rest group, what each
 * part is called — live in `display-set-rules.ts`, a verbatim copy of
 * pacsbin-v2's `shared/utils/display-set-rules.ts`, so a series splits the same
 * way in both apps. Don't edit it here alone. This file adapts it to the
 * uploader: reading the headers out of dcmjs, building the parts, and handing
 * the split to the upload. The v2 viewer only suggests a split, since it can't
 * see the headers until a series is opened, so the upload sends the split along
 * (`displaySetsForUpload`) and a freshly uploaded series opens already split.
 */

const ROWS = "00280010";
const COLUMNS = "00280011";
const MODALITY = "00080060";
// Is a multiframe instance a clip or a volume? See classifyMultiframe.
const FRAME_INCREMENT_POINTER = "00280009";
const FRAME_TIME = "00181063";
const FRAME_TIME_VECTOR = "00181065";
const CINE_RATE = "00180040";
const RECOMMENDED_DISPLAY_FRAME_RATE = "00082144";
const ACTUAL_FRAME_DURATION = "00181242";

function tagValue(instance: Instance, tag: string): any {
  return instance.instanceData.dicomDataset?.dict[tag]?.Value;
}

function intTag(instance: Instance, tag: string): number | null {
  const raw = tagValue(instance, tag)?.[0];
  if (raw == null) return null;
  const parsed = parseInt(String(raw), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/** An instance's decoded matrix as `"<columns>x<rows>"`, or null if unknown. */
export function matrixKeyFor(instance: Instance): string | null {
  const rows = intTag(instance, ROWS);
  const columns = intTag(instance, COLUMNS);
  if (!rows || !columns) return null;
  return `${columns}x${rows}`;
}

/**
 * The matrix shared by every instance in a series, or null when they disagree (or
 * any of them is unknown).
 *
 * After splitting this is never null in practice — every part is matrix-
 * homogeneous by construction — but it stays defensive because the bulk redaction
 * apply keys off it, and "I could not prove these are the same size" has to read
 * as "do not apply", not as a match.
 */
export function seriesMatrixKey(series: Series): string | null {
  let key: string | null = null;
  for (const instance of series.instances) {
    const instanceKey = matrixKeyFor(instance);
    if (instanceKey === null) return null;
    if (key === null) key = instanceKey;
    else if (key !== instanceKey) return null;
  }
  return key;
}

/**
 * The headers the shared rules need, read from an instance's dcmjs dict — the
 * same fields pacsbin-v2 reads from DICOMweb metadata.
 */
function headersFor(
  instance: Instance,
  seriesModality?: string
): DisplaySetHeaders {
  const first = (tag: string) => tagValue(instance, tag)?.[0];
  return {
    instanceId: instance.uid,
    rows: first(ROWS),
    columns: first(COLUMNS),
    numberOfFrames: instance.numberOfFrames,
    modality: first(MODALITY) ?? seriesModality,
    frameIncrementPointer: tagValue(instance, FRAME_INCREMENT_POINTER),
    framesShareOnePlane: framesShareOnePlaneFromDicomJson(
      instance.instanceData.dicomDataset?.dict
    ),
    frameTime: first(FRAME_TIME),
    frameTimeVector: first(FRAME_TIME_VECTOR),
    cineRate: first(CINE_RATE),
    recommendedDisplayFrameRate: first(RECOMMENDED_DISPLAY_FRAME_RATE),
    actualFrameDuration: first(ACTUAL_FRAME_DURATION),
  };
}

/**
 * Split one series into its parts, preserving instance order within each part and
 * ordering the parts by where they first appear.
 *
 * Returns `[series]` unchanged when there is nothing to split, so the common case
 * costs nothing and keeps its identity.
 *
 * Call this at load time, before any editing: parts are built clean, so per-series
 * editor state (trim, redaction boxes, selection) is deliberately not carried over.
 */
export function splitSeries(series: Series): Series[] {
  const detected = detectDisplaySets(
    series.instances.map((instance) => headersFor(instance, series.modality))
  );
  if (!detected) return [series];

  const labels = numberDisplaySetLabels(detected.labels);
  return detected.keys.map((key, index) => ({
    // Derived from the source uid and the part key rather than minted fresh, so
    // re-running the split produces the same uids. The DICOMweb importer loads
    // in batches and calls back in for each one, which collapses and re-splits
    // an already-split series; a random uid here would orphan every reference
    // taken by an earlier batch (markedForRemoval, in particular). The first
    // part keeps the source uid outright, so the common references survive
    // untouched.
    uid: index === 0 ? series.uid : `${series.uid}:${key}`,
    seriesInstanceUID: series.seriesInstanceUID,
    seriesDescription: series.seriesDescription,
    modality: series.modality,
    instances: series.instances.filter(
      (instance) => detected.assignment[instance.uid] === index + 1
    ),
    displaySet: {
      partIndex: index + 1,
      partCount: detected.keys.length,
      label: labels[index],
      baseLabel: detected.labels[index],
    },
  }));
}

/** `splitSeries` across a study's series list, flattened in place order. */
export function splitStudySeries(seriesList: Series[]): Series[] {
  return seriesList.flatMap(splitSeries);
}

/** The key parts share, and the one `collapseSeriesParts` merges them under. */
function collapseKey(series: Series): string {
  // Fall back to the app-level uid so instances from files with no readable
  // SeriesInstanceUID don't all collapse into one another.
  return series.seriesInstanceUID || series.uid;
}

/**
 * Merge split parts back into one series per SeriesInstanceUID, for export.
 *
 * The upload keys the remote series record on SeriesInstanceUID and numbers
 * instance filenames from 1 within each series, under a storage prefix derived
 * from that same UID — so uploading the parts separately would both duplicate the
 * series record and have part 2's `1.dcm.gz` overwrite part 1's.
 */
export function collapseSeriesParts(seriesList: Series[]): Series[] {
  const merged = new Map<string, Series>();
  const order: string[] = [];

  for (const series of seriesList) {
    const key = collapseKey(series);
    const existing = merged.get(key);
    if (existing) {
      existing.instances.push(...series.instances);
    } else {
      merged.set(key, {
        ...series,
        displaySet: undefined,
        instances: [...series.instances],
      });
      order.push(key);
    }
  }

  return order.map((key) => merged.get(key)!);
}

export interface UploadDisplaySets {
  /** Each part's base label, as pacsbin-v2 stores them. */
  parts: string[];
  /** Instance uid -> 1-based part number. */
  partOfInstance: Map<string, number>;
}

/**
 * The split to send with each collapsed series on upload, keyed like
 * `collapseSeriesParts`. pacsbin-v2 stores it so the viewer opens the series
 * already split.
 *
 * Built from the parts as they stand at export, after exclusions have been
 * applied: a part whose images were all excluded is gone from the list, so the
 * survivors are renumbered here rather than trusting `displaySet.partIndex`.
 * Only base labels go up; pacsbin numbers repeated ones ("clip 2 of 3") itself,
 * against the parts that survived.
 * Series left with fewer than two parts get no entry — nothing to split.
 */
export function displaySetsForUpload(
  seriesList: Series[]
): Map<string, UploadDisplaySets> {
  const bySeries = new Map<string, UploadDisplaySets>();

  for (const series of seriesList) {
    if (!series.displaySet || series.instances.length === 0) continue;
    const key = collapseKey(series);
    let entry = bySeries.get(key);
    if (!entry) {
      entry = { parts: [], partOfInstance: new Map() };
      bySeries.set(key, entry);
    }
    entry.parts.push(series.displaySet.baseLabel);
    for (const instance of series.instances) {
      entry.partOfInstance.set(instance.uid, entry.parts.length);
    }
  }

  for (const [key, entry] of bySeries) {
    if (entry.parts.length < 2) bySeries.delete(key);
  }
  return bySeries;
}
